import { draf } from '#components/cz-utils/draf.js';
import type { MatomoConfig } from '#cz/config/types';
import type { Role } from '@neovici/core/context/boot-info.js';

type Paq = unknown[][];

interface TrackEventOpts {
	category: string;
	action: string;
	name?: string;
	value?: number;
}

declare global {
	interface Window {
		_paq?: Paq;
	}
}

const ROLES_MAX_LENGTH = 100,
	toHex = (buffer: ArrayBuffer) =>
		[...new Uint8Array(buffer)]
			.map((byte) => byte.toString(16).padStart(2, '0'))
			.join('');

const sha256$ = async (input: string): Promise<string> =>
	toHex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input)));

const push = (...args: unknown[]) => {
	window._paq = window._paq ?? [];
	window._paq.push(args);
};

export const isMatomoConfigured = () => Boolean(window._paq);

/** Dimensions of the active tracker config — undefined when unconfigured */
export const getMatomoDimensions = () => matomoConfig?.dimensions;

export const trackEvent = ({
	category,
	action,
	name,
	value,
}: TrackEventOpts) => {
	if (!isMatomoConfigured()) {
		return;
	}
	push('trackEvent', category, action, ...(name != null ? [name] : []), value);
};

export const trackPageView = (customUrl?: string) => {
	if (!isMatomoConfigured()) {
		return;
	}
	// delay by a double rAF so the loaded view can set its title
	// (useViewTitle → view-title-update → document.title) before sending —
	// the title travels per-request as trackPageView's first argument
	draf(() => {
		if (customUrl != null) {
			push('setCustomUrl', customUrl);
		}
		push('trackPageView', document.title);
	});
};

export const setDimension = (
	dimension: number | undefined,
	value: string | number | boolean,
) => {
	if (dimension == null || !isMatomoConfigured()) {
		return;
	}
	push('setCustomDimension', dimension, String(value));
};

export const formatRoles = (roles: Role[] | null | undefined): string => {
	const names = [
		...new Set((roles ?? []).map((role) => role.name).filter(Boolean)),
	]
		.sort()
		.join(', ');
	return names.length > 0 ? names.slice(0, ROLES_MAX_LENGTH) : 'none';
};

const hashedUserId$ = async (
	id: string | undefined,
	salt: string | undefined,
): Promise<string | undefined> => {
	if (!id || !salt) {
		return undefined;
	}
	try {
		return await sha256$(`${salt}${id}`);
	} catch {
		// crypto.subtle unavailable (insecure context) — skip pseudonymized id, never send raw
		return undefined;
	}
};

type BootEvent = CustomEvent<{
	currentUser?: { id?: string };
	roles?: Role[] | null;
}>;

const onBoot = ({ detail }: BootEvent) => {
	const { currentUser, roles } = detail ?? {},
		{ dimensions, userIdSalt } = matomoConfig ?? {};
	hashedUserId$(currentUser?.id, userIdSalt).then((userId) => {
		if (userId != null) {
			push('setUserId', userId);
		}
		if (dimensions) {
			setDimension(dimensions.roles, formatRoles(roles));
		}
	});
};

let matomoConfig: MatomoConfig | undefined;

/** Test hook — resets singleton state */
export const resetMatomo = () => {
	matomoConfig = undefined;
	delete window._paq;
	document.removeEventListener('boot', onBoot as EventListener);
};

export const initMatomo = (config: MatomoConfig | undefined) => {
	if (matomoConfig != null || !config?.url || !config.siteId) {
		return;
	}
	matomoConfig = config;

	const { url, siteId, dimensions } = config;
	push('setTrackerUrl', url);
	push('setSiteId', siteId);
	push('disableCookies');
	if (dimensions) {
		setDimension(dimensions.env, config.env ?? window.cz?.config?.env ?? '');
		// window.cz.state is legacy global state (see src/init.js) — not on the Cz type
		setDimension(
			dimensions.myItemsOnly,
			Boolean(
				(window as { cz?: { state?: { myItemsOnly?: boolean } } }).cz?.state
					?.myItemsOnly,
			),
		);
	}
	document.addEventListener('boot', onBoot as EventListener);

	try {
		const script = document.createElement('script');
		script.async = true;
		script.src = new URL('matomo.js', `${url}/../`).href;
		document.head.appendChild(script);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('Failed to load Matomo!', e);
	}
};

/**
 * Builds the page URL for tracking from a matched SPA route:
 * the full route URL is kept verbatim (object ids, filters, tab hash params) —
 * prefixed with the current document path and the `#!` hashbang so the URL
 * matches the app's real deep links: clickable in Matomo reports and
 * preserving the deploy path prefix on preview environments.
 */
export const toPageUrl = (
	url: URL,
	pathname: string = window.location.pathname,
): string => `${pathname}#!${url.pathname}${url.search}${url.hash}`;
